import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Group,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import {
  IconCheck,
  IconDeviceFloppy,
  IconInfoCircle,
  IconPencil,
  IconX,
} from '@tabler/icons-react';
import type { ExpeditorAgreementDto } from '@expeditor/shared';
import { apiClient } from '../../shared/api/client';
import { agreementsApi } from '../../shared/api/agreements';
import { ExpeditorInfoCard } from '../expeditor-profile/ExpeditorInfoCard';
import { useAuthStore } from '../../shared/auth/auth.store';
import { homeRouteForRole } from '../../shared/auth/home-route';
import { ClientFieldsByType } from '../../shared/ui/ClientFieldsByType';
import { EdoSubscriberField } from '../../shared/ui/EdoSubscriberField';
import {
  AddressBreakdownFields,
  useAddressBreakdown,
} from '../../shared/ui/AddressBreakdownFields';

const TYPE_LABEL: Record<string, string> = {
  INDIVIDUAL: 'Физическое лицо',
  ENTREPRENEUR: 'Индивидуальный предприниматель',
  LEGAL_RU: 'Юридическое лицо РФ',
  LEGAL_FOREIGN: 'Иностранная организация',
};

interface MyProfile {
  userId: string;
  email: string;
  emailVerified: boolean;
  client: {
    serviceId: string;
    type: string;
    displayName: string;
    phone: string | null;
    email: string | null;
    actualAddress: string | null;
    edoSubscriberId: string | null;
    individual?: Record<string, unknown> | null;
    entrepreneur?: Record<string, unknown> | null;
    legalRu?: Record<string, unknown> | null;
    legalForeign?: Record<string, unknown> | null;
  };
}

export function ProfilePage() {
  const user = useAuthStore((s) => s.user);
  const [profile, setProfile] = useState<MyProfile | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Редактирование контактов (телефон + фактический адрес).
  const [editing, setEditing] = useState(false);
  const [phone, setPhone] = useState('');
  const [actualAddress, setActualAddress] = useState('');
  const [edoSubscriberId, setEdoSubscriberId] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  // Разбивка фактического адреса (для ЭТрН) + телефон для ЭПД — одна на текст адреса.
  const { parts: addrParts, setParts: setAddrParts, save: saveAddrParts } = useAddressBreakdown(
    profile?.client.actualAddress,
  );
  // Договоры ТЭО клиента — только просмотр (заводит их экспедитор).
  const [agreements, setAgreements] = useState<ExpeditorAgreementDto[]>([]);

  const load = () =>
    apiClient
      .get<MyProfile>('/clients/me')
      .then((r) => {
        setProfile(r.data);
        setPhone(r.data.client.phone ?? '');
        setActualAddress(r.data.client.actualAddress ?? '');
        setEdoSubscriberId(r.data.client.edoSubscriberId ?? '');
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить профиль'));

  useEffect(() => {
    if (user?.role !== 'CLIENT') return;
    void load();
    agreementsApi.listMine().then(setAgreements).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const onCancel = () => {
    if (!profile) return;
    setPhone(profile.client.phone ?? '');
    setActualAddress(profile.client.actualAddress ?? '');
    setEdoSubscriberId(profile.client.edoSubscriberId ?? '');
    setEditing(false);
  };

  const onSave = async () => {
    setError(null);
    setSavedAt(null);
    setSaving(true);
    try {
      await apiClient.patch('/clients/me', {
        phone: phone.trim() || undefined,
        actualAddress: actualAddress.trim() || undefined,
        edoSubscriberId: edoSubscriberId.trim(),
      });
      // Разбивка хранится отдельно, по тексту фактического адреса.
      await saveAddrParts(actualAddress).catch(() => undefined);
      await load();
      setEditing(false);
      setSavedAt(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  if (user && user.role !== 'CLIENT') {
    return <Navigate to={homeRouteForRole(user.role)} replace />;
  }

  // ИНН клиента из его подтипа — по нему кнопка «Найти» ищет абонента ЭДО.
  const sub = profile?.client;
  const clientInn =
    ((sub?.legalRu?.inn as string | undefined) ??
      (sub?.entrepreneur?.inn as string | undefined) ??
      (sub?.legalForeign?.taxId as string | undefined)) ||
    null;

  return (
    <Stack>
      <Title order={2}>Профиль</Title>
      {error && (
        <Alert color="red" icon={<IconInfoCircle size={16} />}>
          {error}
        </Alert>
      )}
      {!profile && !error && <Text c="dimmed">Загрузка…</Text>}
      {profile && (
        <Stack>
          <Card withBorder>
            <Stack gap="xs">
              <Group justify="space-between" wrap="nowrap">
                <Text fw={500} size="lg">
                  {profile.client.displayName}
                </Text>
                <Code>{profile.client.serviceId}</Code>
              </Group>
              <Group gap="xs">
                <Badge variant="light">
                  {TYPE_LABEL[profile.client.type] ?? profile.client.type}
                </Badge>
                {profile.emailVerified ? (
                  <Badge variant="light" color="green">
                    email подтверждён
                  </Badge>
                ) : (
                  <Badge variant="light" color="yellow">
                    email не подтверждён
                  </Badge>
                )}
              </Group>
              <Text size="sm" c="dimmed">
                Email учётной записи: {profile.email}
              </Text>
            </Stack>
          </Card>

          <Card withBorder>
            <Group justify="space-between" mb="sm">
              <Title order={4}>Контакты</Title>
              {!editing ? (
                <Button
                  size="xs"
                  variant="subtle"
                  leftSection={<IconPencil size={14} />}
                  onClick={() => setEditing(true)}
                >
                  Редактировать
                </Button>
              ) : (
                <Group gap="xs">
                  <Button
                    size="xs"
                    variant="subtle"
                    color="gray"
                    leftSection={<IconX size={14} />}
                    onClick={onCancel}
                    disabled={saving}
                  >
                    Отмена
                  </Button>
                  <Button
                    size="xs"
                    leftSection={<IconDeviceFloppy size={14} />}
                    onClick={onSave}
                    loading={saving}
                  >
                    Сохранить
                  </Button>
                </Group>
              )}
            </Group>

            {savedAt && !editing && (
              <Alert color="green" icon={<IconCheck size={16} />} mb="sm">
                Сохранено в {savedAt.toLocaleTimeString('ru-RU')}.
              </Alert>
            )}

            {editing ? (
              <Stack gap="md">
                <TextInput
                  label="Контактный телефон"
                  description="По этому номеру с вами свяжется менеджер экспедитора"
                  placeholder="+7 ..."
                  value={phone}
                  onChange={(e) => setPhone(e.currentTarget.value)}
                />
                <Textarea
                  label="Фактический адрес"
                  description="Текущее место нахождения. Может отличаться от юридического / адреса регистрации."
                  placeholder="г. Москва, ул. ..."
                  value={actualAddress}
                  onChange={(e) => setActualAddress(e.currentTarget.value)}
                  minRows={2}
                  autosize
                />
                <AddressBreakdownFields
                  value={addrParts}
                  onChange={setAddrParts}
                  hint="Заполните для ЭТрН: адрес уйдёт в документ структурированно (АдрРФ), телефон — в контакт. Подставится при создании ЭТрН для этого адреса."
                />
                <EdoSubscriberField
                  value={edoSubscriberId}
                  onChange={setEdoSubscriberId}
                  inn={clientInn}
                  description="Ваш идентификатор в ЭДО.МИГ24 — по нему экспедитор шлёт вам электронные документы. «Найти» ищет по вашему ИНН."
                />
              </Stack>
            ) : (
              <Stack gap="xs">
                <div>
                  <Text size="xs" c="dimmed">
                    Контактный телефон
                  </Text>
                  <Text size="sm">{profile.client.phone || '—'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">
                    Фактический адрес
                  </Text>
                  <Text size="sm">{profile.client.actualAddress || '—'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">
                    Код абонента ЭДО
                  </Text>
                  <Text size="sm">{profile.client.edoSubscriberId || '—'}</Text>
                </div>
              </Stack>
            )}
          </Card>

          <Card withBorder>
            <Title order={4} mb="sm">
              Реквизиты
            </Title>
            <ClientFieldsByType
              type={profile.client.type}
              fields={
                profile.client.individual ??
                profile.client.entrepreneur ??
                profile.client.legalRu ??
                profile.client.legalForeign ??
                null
              }
            />
          </Card>

          {/* Договоры ТЭО — только просмотр: заводит и архивирует их экспедитор. */}
          <Card withBorder>
            <Title order={4} mb={4}>
              Договоры с экспедитором
            </Title>
            <Text size="xs" c="dimmed" mb="sm">
              Рамочные договоры транспортной экспедиции. На их основании выдаются поручения;
              заводит договоры экспедитор.
            </Text>
            {agreements.length === 0 ? (
              <Text size="sm" c="dimmed">
                Договоров пока нет — обратитесь к вашему менеджеру.
              </Text>
            ) : (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Номер</Table.Th>
                    <Table.Th>Подписан</Table.Th>
                    <Table.Th>Действует до</Table.Th>
                    <Table.Th>Предмет</Table.Th>
                    <Table.Th>Статус</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {agreements.map((a) => (
                    <Table.Tr key={a.id}>
                      <Table.Td>№ {a.number}</Table.Td>
                      <Table.Td>{a.signedDate.slice(0, 10)}</Table.Td>
                      <Table.Td>{a.validUntil ? a.validUntil.slice(0, 10) : 'бессрочный'}</Table.Td>
                      <Table.Td>
                        <Text size="sm">{a.subject || '—'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={a.isActive ? 'green' : 'gray'} variant="light">
                          {a.isActive ? 'действует' : a.archivedAt ? 'в архиве' : 'срок истёк'}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Card>

          <ExpeditorInfoCard />
        </Stack>
      )}
    </Stack>
  );
}
